import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { TpHitRateBar } from "./chart-data";
import { CHART_COLORS, CHART_FONT, CSS_VARS, useChartTheme } from "./chart-theme";

interface Props {
	data: TpHitRateBar[];
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: TpHitRateBar }>;
}) => {
	if (!active || !payload?.[0]) return null;
	const p = payload[0].payload;
	return (
		<div
			className="rounded-md border px-2.5 py-1.5 text-[0.72rem] shadow-lg"
			style={{
				background: CSS_VARS.tooltip,
				borderColor: CSS_VARS.tooltipBorder,
			}}
		>
			<p className="font-semibold tg-text-primary">
				{p.level}: {p.hitRatePercent}%
			</p>
		</div>
	);
};

const barColor = (rate: number): string => {
	if (rate >= 0.7) return CHART_COLORS.green;
	if (rate >= 0.4) return CHART_COLORS.yellow;
	return CHART_COLORS.red;
};

export const TpHitRateChart = ({ data }: Props) => {
	const theme = useChartTheme();

	if (data.length === 0) {
		return (
			<p className="flex h-full items-center justify-center text-[0.78rem] tg-text-muted">
				No TP level data available.
			</p>
		);
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data}
				margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
			>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke={theme.grid}
					vertical={false}
				/>
				<XAxis
					dataKey="level"
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={{ stroke: theme.grid }}
				/>
				<YAxis
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={false}
					width={40}
					tickFormatter={(v: number) => `${v}%`}
					domain={[0, 100]}
				/>
				<Tooltip
					content={<CustomTooltip />}
					cursor={{ fill: theme.grid, fillOpacity: 0.5 }}
				/>
				<Bar dataKey="hitRatePercent" radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
					{data.map((entry) => (
						<Cell key={entry.level} fill={barColor(entry.hitRate)} />
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};
